import React, { useState } from 'react';
import { Suscriptor, Plan } from '../types/Suscriptor';
import { useAuth } from '../contexts/AuthContext';
import { textoSobre } from '../utils/color';
import { Badge, BadgeTone, Button, Card, IconSwap, cn } from './ui';
import { CheckIcon, CopyIcon } from './icons';

interface SubscriberCardProps {
  suscriptor: Suscriptor;
  planes?: Plan[];
}

/** Color por defecto cuando Salesforce no manda `colorPlan`. */
const COLOR_PLAN_FALLBACK = '#264F6B';

const tonoEstado = (estado: string | null): BadgeTone => {
  switch (estado?.toLowerCase()) {
    case 'activo':
      return 'positive';
    case 'pendiente':
      return 'warning';
    case 'inactivo':
    case 'baja':
    case 'cancelado':
      return 'negative';
    default:
      return 'neutral';
  }
};

/** Unidades tal como las manda Salesforce, para pasarlas a castellano. */
const UNIDADES_EDAD: Record<string, string> = {
  year: 'año',
  years: 'años',
  month: 'mes',
  months: 'meses',
  week: 'semana',
  weeks: 'semanas',
  day: 'día',
  days: 'días',
};

/**
 * `edad` no es un número: Salesforce la manda ya armada y en inglés ("4 Years",
 * "11 Months" para bebés). Se traducen sólo las unidades conocidas y cualquier
 * otra cosa se muestra tal cual, en vez de pegarle un "años" que puede ser falso.
 */
const formatearEdad = (edad: number | string | null): string => {
  if (edad === null || edad === undefined || edad === '') return '—';
  if (typeof edad === 'number') return `${edad} ${edad === 1 ? 'año' : 'años'}`;
  return edad.replace(/[A-Za-z]+/g, (palabra) => UNIDADES_EDAD[palabra.toLowerCase()] ?? palabra).trim();
};

export const formatearFecha = (fecha: string | null): string => {
  if (!fecha) return '—';
  // Fecha sin hora (ej: nacimiento): no tiene zona, se arma partiendo el string.
  // Con new Date('YYYY-MM-DD') se toma como UTC y en Argentina se ve un día antes.
  if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
    const [y, m, d] = fecha.split('-');
    return `${d}/${m}/${y}`;
  }
  try {
    return new Date(fecha).toLocaleDateString('es-AR', {
      timeZone: 'America/Argentina/Buenos_Aires',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return 'Fecha inválida';
  }
};

interface CampoProps {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}

/** Par etiqueta/valor. Misma tipografía en toda la ficha para que se escanee en vertical. */
const Campo: React.FC<CampoProps> = ({ label, children, mono = false }) => (
  <div className="min-w-0">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <div className={cn('mt-0.5 text-sm font-medium text-gappex-secondary', mono && 'tabular-nums')}>
      {children}
    </div>
  </div>
);

interface DescuentoTileProps {
  etiqueta: string;
  pct: number | null;
}

/** Porcentaje de descuento en grande; sin valor muestra un guión, no un 0%. */
const DescuentoTile: React.FC<DescuentoTileProps> = ({ etiqueta, pct }) => (
  <div
    className="flex flex-col items-center justify-center gap-0.5 rounded-xl bg-slate-50 px-2 py-3 text-center"
    role="img"
    aria-label={`${etiqueta}: ${pct !== null ? `${pct}% de descuento` : 'sin descuento'}`}
  >
    {pct !== null ? (
      <span aria-hidden="true" className="text-2xl font-extrabold leading-none tabular-nums text-gappex-dark">
        {pct}%
      </span>
    ) : (
      <span aria-hidden="true" className="text-lg font-bold leading-none text-slate-400">
        —
      </span>
    )}
    <span aria-hidden="true" className="mt-1 text-[11px] uppercase tracking-wide text-slate-500">
      {etiqueta}
    </span>
  </div>
);

interface PlanActivoCardProps {
  plan: Plan;
  esFarmacia: boolean;
}

/**
 * Tarjeta del plan vigente. El color lo manda Salesforce (`colorPlan`), así que
 * se usa igual que en /planes: como banda de identidad con el texto calculado
 * por contraste, nunca tiñendo la superficie entera. Los hex de plan van de un
 * cian brillante a un gris casi negro; un fondo al 10% de cualquiera de ellos
 * termina siendo el mismo gris sucio y pierde justamente lo que distingue al plan.
 */
const PlanActivoCard: React.FC<PlanActivoCardProps> = ({ plan, esFarmacia }) => {
  const [copiado, setCopiado] = useState(false);
  const color = plan.colorPlan || COLOR_PLAN_FALLBACK;
  const colorTexto = textoSobre(color);

  const copiarCredencial = async () => {
    const texto = plan.numeroMiembro;
    if (!texto) return;

    const marcarCopiado = () => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    };

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(texto);
        marcarCopiado();
        return;
      }
      throw new Error('Clipboard API no disponible');
    } catch {
      // Safari sin permisos y contextos no seguros no exponen la Clipboard API.
      try {
        const textArea = document.createElement('textarea');
        textArea.value = texto;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        if (document.execCommand('copy')) marcarCopiado();
        document.body.removeChild(textArea);
      } catch (err) {
        console.error('No se pudo copiar la credencial', err);
      }
    }
  };

  const descuentos = plan.descuentos;
  const tienePremium = plan.tipoSuscripcion?.toLowerCase() === 'premium';

  return (
    <Card as="section" className="p-5">
      <div
        className="flex items-center justify-between gap-3 rounded-lg px-4 py-3"
        style={{ backgroundColor: color, color: colorTexto }}
      >
        <span className="truncate font-poppins text-lg font-bold leading-tight">
          {plan.nombrePlanId || 'Plan sin nombre'}
        </span>
        <span
          className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide"
          style={{ backgroundColor: colorTexto === '#ffffff' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)' }}
        >
          Activo
        </span>
      </div>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        <Campo label="Credencial" mono>
          {plan.numeroMiembro || '—'}
        </Campo>
        {plan.numeroMiembro && (
          <Button
            variant="secondary"
            onClick={copiarCredencial}
            aria-label={copiado ? 'Credencial copiada' : 'Copiar credencial'}
            className={cn(copiado && 'text-emerald-600')}
            icon={
              <IconSwap swapKey={copiado ? 'ok' : 'copy'}>
                {copiado ? <CheckIcon /> : <CopyIcon />}
              </IconSwap>
            }
          >
            {copiado ? 'Copiado' : 'Copiar'}
          </Button>
        )}
      </div>

      {esFarmacia ? (
        <div className="mt-4 border-t border-slate-100 pt-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Descuento en farmacias adheridas
          </p>
          <div className="mt-1.5">
            <Badge tone={tienePremium ? 'positive' : 'negative'}>
              {tienePremium ? 'Sí, corresponde descuento' : 'No corresponde descuento'}
            </Badge>
          </div>
        </div>
      ) : (
        descuentos && (
          <div className="mt-4 border-t border-slate-100 pt-4">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Descuentos del plan
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <DescuentoTile etiqueta="Ambulatorio" pct={descuentos.ambulatorio} />
              <DescuentoTile etiqueta="Cirugías" pct={descuentos.cirugias} />
              <DescuentoTile etiqueta="Internación" pct={descuentos.internacion} />
              <DescuentoTile etiqueta="Medicamentos" pct={descuentos.medicamentos} />
            </div>
          </div>
        )
      )}
    </Card>
  );
};

const SubscriberCard: React.FC<SubscriberCardProps> = ({ suscriptor, planes }) => {
  const { userRole } = useAuth();
  const esFarmacia = userRole === 'farmacia';

  // Se listan todos los planes vigentes, no sólo el primero: un suscriptor
  // puede tener más de uno y esconderlos lleva a informar cobertura de menos.
  const planesActivos = (planes ?? []).filter(
    (plan) => plan.estadoPlan?.toLowerCase() === 'activo',
  );

  const nombreCompleto = [suscriptor.nombre, suscriptor.apellido].filter(Boolean).join(' ') || '—';

  return (
    <div className="space-y-4">
      {planesActivos.length > 0 ? (
        planesActivos.map((plan) => (
          <PlanActivoCard key={plan.id} plan={plan} esFarmacia={esFarmacia} />
        ))
      ) : (
        <Card className="flex items-center gap-3 p-5">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" />
          <p className="text-sm text-slate-500">
            El suscriptor no tiene planes vigentes.
          </p>
        </Card>
      )}

      <Card as="section" className="p-6">
        <h2 className="font-poppins text-base font-semibold text-gappex-secondary">
          Datos del suscriptor
        </h2>
        <p className="mt-1 text-xs text-slate-400">Información registrada en Salesforce.</p>

        <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
          <Campo label="Nombre completo">{nombreCompleto}</Campo>
          <Campo label="Documento" mono>
            {suscriptor.tipoDocumento ? `${suscriptor.tipoDocumento} ` : ''}
            {suscriptor.numeroDocumento || '—'}
          </Campo>
          <Campo label="Fecha de nacimiento" mono>
            {formatearFecha(suscriptor.fechaNacimiento)}
          </Campo>
          <Campo label="Edad" mono>
            {formatearEdad(suscriptor.edad)}
          </Campo>
          <Campo label="E-mail">
            <span className="break-words">{suscriptor.email || '—'}</span>
          </Campo>
          <Campo label="Teléfono" mono>
            {suscriptor.telefono || '—'}
          </Campo>
          <Campo label="Estado">
            {suscriptor.estado ? (
              <Badge tone={tonoEstado(suscriptor.estado)}>{suscriptor.estado}</Badge>
            ) : (
              '—'
            )}
          </Campo>
        </div>
      </Card>
    </div>
  );
};

export default SubscriberCard;
